import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Download, FileSignature, Loader2, Upload } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { getDocument, Util } from 'pdfjs-dist';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import toast from 'react-hot-toast';

const VIEW_SCALE = 1.5;

const PDFEditor = ({ isLoading: externalLoading, setIsLoading }) => {
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfBytes, setPdfBytes] = useState(null);
  const [pdfDocument, setPdfDocument] = useState(null);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState({ width: 0, height: 0 });
  const [textBlocks, setTextBlocks] = useState([]);
  const [edits, setEdits] = useState({});
  const [loading, setLoading] = useState(false);
  const canvasRef = useRef(null);
  const renderTaskRef = useRef(null);
  const isLoading = externalLoading || loading;

  const updateLoading = useCallback((value) => {
    setLoading(value);
    setIsLoading?.(value);
  }, [setIsLoading]);

  const loadPDF = useCallback(async (file) => {
    updateLoading(true);
    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      const document = await getDocument({ data: bytes.slice() }).promise;
      setPdfFile(file);
      setPdfBytes(bytes);
      setPdfDocument(document);
      setCurrentPageIndex(0);
      setEdits({});
      toast.success(`PDF loaded (${document.numPages} page${document.numPages === 1 ? '' : 's'})`);
    } catch (error) {
      console.error(error);
      toast.error('Unable to open this PDF.');
    } finally {
      updateLoading(false);
    }
  }, [updateLoading]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'application/pdf': ['.pdf'] },
    multiple: false,
    onDrop: (files) => files[0] && loadPDF(files[0])
  });

  useEffect(() => {
    if (!pdfDocument || !canvasRef.current) return undefined;
    let cancelled = false;

    const renderPage = async () => {
      updateLoading(true);
      try {
        renderTaskRef.current?.cancel();
        const pageNumber = currentPageIndex + 1;
        const page = await pdfDocument.getPage(pageNumber);
        const viewport = page.getViewport({ scale: VIEW_SCALE });
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        setPageSize({ width: viewport.width, height: viewport.height });

        const renderTask = page.render({ canvasContext: context, viewport });
        renderTaskRef.current = renderTask;
        await renderTask.promise;

        const textContent = await page.getTextContent();
        if (cancelled) return;
        const blocks = textContent.items
          .filter((item) => item.str && item.str.trim())
          .map((item, index) => {
            const transform = Util.transform(viewport.transform, item.transform);
            const fontHeight = Math.max(8, Math.hypot(transform[2], transform[3]));
            return {
              id: `${pageNumber}-${index}`,
              pageIndex: currentPageIndex,
              text: item.str,
              x: transform[4],
              y: transform[5] - fontHeight,
              width: Math.max(item.width * VIEW_SCALE, 4),
              height: fontHeight * 1.2,
              fontSize: fontHeight,
              pdfX: item.transform[4],
              pdfY: item.transform[5],
              pdfWidth: item.width,
              pdfHeight: Math.max(1, Math.hypot(item.transform[2], item.transform[3]))
            };
          });
        setTextBlocks(blocks);
      } catch (error) {
        if (error?.name !== 'RenderingCancelledException') {
          console.error(error);
          toast.error('Unable to render this PDF page.');
        }
      } finally {
        if (!cancelled) updateLoading(false);
      }
    };

    renderPage();
    return () => {
      cancelled = true;
      renderTaskRef.current?.cancel();
    };
  }, [currentPageIndex, pdfDocument, updateLoading]);

  const sampleBackgroundColor = (block) => {
    const context = canvasRef.current?.getContext('2d');
    if (!context) return { r: 1, g: 1, b: 1, css: '#ffffff' };

    const points = [
      [block.x + 2, block.y - 2],
      [block.x + (block.width / 2), block.y - 2],
      [block.x + block.width - 2, block.y - 2],
      [block.x + 2, block.y + block.height + 2],
      [block.x + (block.width / 2), block.y + block.height + 2],
      [block.x + block.width - 2, block.y + block.height + 2]
    ];
    const samples = points.map(([x, y]) => {
      const safeX = Math.max(0, Math.min(canvasRef.current.width - 1, Math.round(x)));
      const safeY = Math.max(0, Math.min(canvasRef.current.height - 1, Math.round(y)));
      return Array.from(context.getImageData(safeX, safeY, 1, 1).data).slice(0, 3);
    });
    const median = (channel) => samples.map((sample) => sample[channel]).sort((a, b) => a - b)[Math.floor(samples.length / 2)];
    const red = median(0);
    const green = median(1);
    const blue = median(2);
    return {
      r: red / 255,
      g: green / 255,
      b: blue / 255,
      css: `rgb(${red}, ${green}, ${blue})`
    };
  };

  const updateText = (block, value) => {
    setEdits((previous) => {
      const next = { ...previous };
      if (value === block.text) delete next[block.id];
      else next[block.id] = { ...block, newText: value, backgroundColor: sampleBackgroundColor(block) };
      return next;
    });
  };

  const downloadEditedPDF = async () => {
    if (!pdfBytes) return;
    updateLoading(true);
    try {
      const output = await PDFDocument.load(pdfBytes.slice());
      const font = await output.embedFont(StandardFonts.Helvetica);
      Object.values(edits).forEach((edit) => {
        const page = output.getPage(edit.pageIndex);
        const fontSize = Math.max(5, edit.pdfHeight);
        const lineHeight = fontSize * 1.2;
        const safeText = `${edit.newText || ''}`
          .replace(/\r\n?/g, '\n')
          .split('')
          .map((character) => {
            if (character === '\n') return character;
            try {
              font.encodeText(character);
              return character;
            } catch (_) {
              return '?';
            }
          })
          .join('');
        const lines = safeText.split('\n');
        const replacementWidth = Math.max(
          edit.pdfWidth,
          ...lines.map((line) => font.widthOfTextAtSize(line || ' ', fontSize))
        );
        const background = edit.backgroundColor || { r: 1, g: 1, b: 1 };
        page.drawRectangle({
          x: edit.pdfX,
          y: edit.pdfY - (lineHeight * Math.max(0, lines.length - 1)) - (edit.pdfHeight * 0.25),
          width: replacementWidth,
          height: Math.max(edit.pdfHeight * 1.25, lineHeight * lines.length),
          color: rgb(background.r, background.g, background.b)
        });
        lines.forEach((line, lineIndex) => {
          if (!line) return;
          page.drawText(line, {
            x: edit.pdfX,
            y: edit.pdfY - (lineIndex * lineHeight),
            size: fontSize,
            font,
            color: rgb(0, 0, 0)
          });
        });
      });
      const saved = await output.save();
      const blob = new Blob([saved], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${pdfFile?.name?.replace(/\.pdf$/i, '') || 'document'}-edited.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success('Edited PDF downloaded.');
    } catch (error) {
      console.error(error);
      toast.error('Unable to create the edited PDF.');
    } finally {
      updateLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-slate-100">
      <header className="flex items-center justify-between border-b bg-white px-4 py-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Direct PDF Text Editor</h1>
          <p className="text-xs text-slate-500">Click selectable text on the page and type directly. No OCR or AI is used.</p>
        </div>
        {pdfDocument && (
          <button
            type="button"
            onClick={downloadEditedPDF}
            disabled={isLoading}
            className="flex items-center gap-2 rounded-lg bg-[#232751] px-4 py-2 text-white hover:bg-[#1b1f43] disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            Download edited PDF
          </button>
        )}
      </header>

      {!pdfDocument ? (
        <main className="flex flex-1 items-center justify-center p-6">
          <div
            {...getRootProps()}
            className={`w-full max-w-md cursor-pointer rounded-xl border-2 border-dashed p-12 text-center ${
              isDragActive ? 'border-[#232751] bg-[#232751]/5' : 'border-slate-300 bg-white hover:border-[#232751]/70'
            }`}
          >
            <input {...getInputProps()} />
            <div className="mb-4 flex justify-center gap-2">
              <Upload className="h-8 w-8 text-[#232751]" />
              <FileSignature className="h-8 w-8 text-slate-400" />
            </div>
            <h2 className="text-lg font-medium text-slate-900">Open a text-based PDF</h2>
            <p className="mt-2 text-sm text-slate-600">Drop a PDF here or click to choose one.</p>
          </div>
        </main>
      ) : (
        <>
          <div className="flex items-center justify-center gap-4 border-b bg-white px-4 py-2">
            <button type="button" disabled={currentPageIndex === 0} onClick={() => setCurrentPageIndex((page) => page - 1)} className="rounded p-2 hover:bg-slate-100 disabled:opacity-30">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <span className="text-sm font-medium">Page {currentPageIndex + 1} of {pdfDocument.numPages}</span>
            <button type="button" disabled={currentPageIndex >= pdfDocument.numPages - 1} onClick={() => setCurrentPageIndex((page) => page + 1)} className="rounded p-2 hover:bg-slate-100 disabled:opacity-30">
              <ChevronRight className="h-5 w-5" />
            </button>
            <span className="text-xs text-slate-500">{Object.keys(edits).length} edit{Object.keys(edits).length === 1 ? '' : 's'}</span>
          </div>
          <main className="relative flex flex-1 justify-center overflow-auto p-6">
            {isLoading && <div className="fixed z-50 flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm text-white shadow-lg"><Loader2 className="h-4 w-4 animate-spin" />Loading…</div>}
            <div className="relative bg-white shadow-xl" style={{ width: pageSize.width, height: pageSize.height }}>
              <canvas ref={canvasRef} className="block" />
              {textBlocks.map((block) => (
                <div
                  key={block.id}
                  contentEditable
                  suppressContentEditableWarning
                  spellCheck
                  onBlur={(event) => updateText(block, event.currentTarget.innerText)}
                  className={`absolute box-border overflow-visible whitespace-pre px-0 caret-[#232751] outline-none focus:bg-white focus:text-black focus:ring-1 focus:ring-[#232751] ${
                    edits[block.id]
                      ? 'z-10 bg-white text-black'
                      : 'bg-transparent text-transparent hover:bg-[#232751]/10'
                  }`}
                  style={{
                    left: block.x,
                    top: block.y,
                    minWidth: block.width,
                    minHeight: block.height,
                    fontSize: block.fontSize,
                    lineHeight: `${block.height}px`,
                    ...(edits[block.id]?.backgroundColor ? { backgroundColor: edits[block.id].backgroundColor.css } : {})
                  }}
                >
                  {edits[block.id]?.newText ?? block.text}
                </div>
              ))}
            </div>
          </main>
        </>
      )}
    </div>
  );
};

export default PDFEditor;
